import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Tag, TrendingUp, TrendingDown, AlertTriangle, Download, 
  FileSpreadsheet, CheckCircle, XCircle, IndianRupee, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import TranslatedText from '../components/TranslatedText';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoIQDashboard = () => {
  const [searchParams] = useSearchParams();
  const runId = searchParams.get('run_id');
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const { languageConfig } = useLanguage();

  useEffect(() => {
    fetchDashboardData();
  }, [runId]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/agent-dashboards/promo-iq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (dataType, label) => {
    try {
      setExporting(dataType);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/agent-dashboards/promo-iq/export?data_type=${dataType}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${dataType}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${label} exported successfully`);
    } catch (err) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '₹0';
    if (typeof value === 'string' && value.startsWith('₹')) return value;
    const num = parseFloat(value);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} L`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
    return `₹${num.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !data?.hasData) {
    return (
      <Card className="m-6">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
          <p className="text-lg text-slate-600">{error || 'No PromoIQ analysis found. Please run the agent first.'}</p>
        </CardContent>
      </Card>
    );
  }

  const overview = data?.promoOverview || {};
  const effectiveness = data?.promoEffectiveness || [];
  const categoryAnalysis = data?.categoryPromoAnalysis || [];

  return (
    <div className="space-y-6 p-6" data-testid="promo-iq-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-purple-600" />
            <TranslatedText text="PromoIQ Dashboard" cacheKey="promoiq:title" />
          </h1>
          <p className="text-slate-500 text-sm mt-1" style={{ fontFamily: languageConfig?.font }}>
            <TranslatedText text="Promotion Effectiveness & Discount Governance Analysis" cacheKey="promoiq:subtitle" />
          </p>
        </div>
        <Button onClick={() => handleExport('all_promos', 'All Promos')} disabled={exporting === 'all_promos'}>
          <Download className="h-4 w-4 mr-2" />
          Export Full Analysis
        </Button>
      </div>

      {/* Overview Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Tag className="h-4 w-4" />
              Total Promos
            </div>
            <p className="text-2xl font-bold text-slate-900">{overview.totalPromos || 0}</p>
            <p className="text-xs text-slate-400">{overview.activePromos || 0} active</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <IndianRupee className="h-4 w-4" />
              Promo Revenue
            </div>
            <p className="text-2xl font-bold text-green-600">{overview.totalPromoRevenueFormatted || formatCurrency(overview.totalPromoRevenue)}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <CheckCircle className="h-4 w-4 text-green-500" />
              Effective Promos
            </div>
            <p className="text-2xl font-bold text-green-600">{overview.effectivePromos || 0}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <XCircle className="h-4 w-4 text-red-500" />
              Failed Promos
            </div>
            <p className="text-2xl font-bold text-red-600">{overview.failedPromos || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Promo Effectiveness Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              Promotion Effectiveness
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('promo_effectiveness', 'Promo Effectiveness')}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {effectiveness.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Promo ID</th>
                    <th className="px-3 py-2 text-left font-semibold">Name</th>
                    <th className="px-3 py-2 text-left font-semibold">Type</th>
                    <th className="px-3 py-2 text-left font-semibold">Result</th>
                    <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                    <th className="px-3 py-2 text-right font-semibold">Discount %</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {effectiveness.slice(0, 10).map((promo, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-mono">{promo.promoId || '-'}</td>
                      <td className="px-3 py-2">{promo.promoName || '-'}</td>
                      <td className="px-3 py-2">{promo.promoType || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          promo.result === 'EFFECTIVE_PROMO' ? 'bg-green-100 text-green-700' :
                          promo.result === 'PROMO_FAILURE' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {promo.result || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium">{promo.revenueFormatted || formatCurrency(promo.revenue)}</td>
                      <td className="px-3 py-2 text-right">{promo.discountRate?.toFixed(1) || 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No promotion data available</p>
          )}
        </CardContent>
      </Card>

      {/* Category Analysis */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Percent className="h-5 w-5 text-blue-600" />
              Category Promotion Analysis
            </CardTitle>
            <Button size="sm" variant="outline" onClick={() => handleExport('category_analysis', 'Category Analysis')}>
              <FileSpreadsheet className="h-4 w-4 mr-1" />Export
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {categoryAnalysis.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Category</th>
                    <th className="px-3 py-2 text-right font-semibold">Promo Count</th>
                    <th className="px-3 py-2 text-right font-semibold">Revenue</th>
                    <th className="px-3 py-2 text-right font-semibold">Avg Discount %</th>
                    <th className="px-3 py-2 text-left font-semibold">Effectiveness</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {categoryAnalysis.slice(0, 10).map((cat, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium">{cat.category || '-'}</td>
                      <td className="px-3 py-2 text-right">{cat.promoCount || 0}</td>
                      <td className="px-3 py-2 text-right font-medium">{cat.revenueFormatted || formatCurrency(cat.revenue)}</td>
                      <td className="px-3 py-2 text-right">{cat.avgDiscountRate?.toFixed(1) || 0}%</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          cat.effectiveness === 'HIGH' ? 'bg-green-100 text-green-700' :
                          cat.effectiveness === 'LOW' ? 'bg-red-100 text-red-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {cat.effectiveness || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No category analysis available</p>
          )}
        </CardContent>
      </Card>

      {/* Action Plan */}
      {data?.actionPlan && Object.keys(data.actionPlan).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Action Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {data.actionPlan.P0_Immediate?.length > 0 && (
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-red-600 mb-2">P0 - Immediate</h4>
                  {data.actionPlan.P0_Immediate.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-red-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P1_ThisWeek?.length > 0 && (
                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold text-orange-600 mb-2">P1 - This Week</h4>
                  {data.actionPlan.P1_ThisWeek.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-orange-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.actionPlan.P2_NextCycle?.length > 0 && (
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-blue-600 mb-2">P2 - Next Cycle</h4>
                  {data.actionPlan.P2_NextCycle.slice(0, 3).map((action, idx) => (
                    <div key={idx} className="text-sm p-2 bg-blue-50 rounded mb-2">
                      <p className="font-medium">{action.action}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PromoIQDashboard;
